import React, { createElement } from 'react';

import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';

import { DEFAULT_FIRST_TOKEN, DEFAULT_SECOND_TOKEN } from '@/constants';
import NotFound404 from '@/pages/404';
import HistorySkeleton from '@/pages/History/HistorySkeleton';
import SwapLoadingSkeleton from '@/pages/Swap/SwapLoadingSkeleton';
import { findTokenCombinationFromUrl } from '@/utils/findTokenCombinationFromUrl';
import withSuspense from '@/utils/withSuspense';

import { Layout } from '../../layout/Layout';

const UnlockPage = React.lazy(() => import('../../pages/UnlockPage'));
const History = React.lazy(() => import('../../pages/History'));
const Earn = React.lazy(() => import('../../pages/Earn'));
const Swap = React.lazy(() => import('../../pages/Swap'));
const Burn = React.lazy(() => import('../../pages/Burn'));
const Proposals = React.lazy(() => import('../../pages/Proposals'));
const Statistics = React.lazy(() => import('../../pages/Statistics'));
const MMConfig = React.lazy(() => import('../../pages/MMConfig'));
const SwapPanel = withSuspense(Swap, SwapLoadingSkeleton);
const EarnPanel = withSuspense(Earn);
const HistoryPanel = withSuspense(History, HistorySkeleton);
const StatisticsPanel = withSuspense(Statistics, () => <div />);

const RedirectToSwap = ({ prefix = '' }: { prefix?: string }) => {
  const { pathname } = useLocation();
  const [firstToken = DEFAULT_FIRST_TOKEN, secondToken = DEFAULT_SECOND_TOKEN] = findTokenCombinationFromUrl(pathname);
  return <Navigate replace to={`${prefix}/swap/${firstToken}-${secondToken}`} />;
};

const RedirectToSwapFromTokenCombination = () => {
  const { urlTokenCombination = '' } = useParams();
  const [firstToken, secondToken] = findTokenCombinationFromUrl(urlTokenCombination);
  if (firstToken && secondToken) {
    return <Navigate replace to={`/swap/${firstToken}-${secondToken}`} />;
  }
  return <NotFound404 />;
};

const ValidateSwapTokenCombination = () => {
  const { urlTokenCombination = '' } = useParams();
  const [firstToken, secondToken] = findTokenCombinationFromUrl(urlTokenCombination);
  if (!firstToken || !secondToken) {
    return <NotFound404 />;
  }
  return <SwapPanel />;
};

export const Router = () => {
  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<RedirectToSwap />} />
        <Route path="swap" element={<RedirectToSwap />} />
        {/* temporary for backwards compatibility */}
        <Route path=":urlTokenCombination" element={<RedirectToSwapFromTokenCombination />} />
        {/* temporary for backwards compatibility */}
        <Route path="swap/:urlTokenCombination" element={<ValidateSwapTokenCombination />} />
        <Route path="earn" element={<EarnPanel />} />
        <Route path="history" element={<HistoryPanel />} />
        <Route path="statistics" element={<StatisticsPanel />} />

        <Route path="pg-wallet">
          <Route index element={<RedirectToSwap prefix="/pg-wallet" />} />
          <Route path="swap/:urlTokenCombination" element={<ValidateSwapTokenCombination />} />
          <Route path="history" element={<HistoryPanel />} />
        </Route>
        <Route path="/burn" element={createElement(withSuspense(Burn))} />
        <Route path="/proposals" element={createElement(withSuspense(Proposals))} />

        <Route path="*" element={<NotFound404 />} />
      </Route>

      <Route path="/unlock" element={createElement(withSuspense(UnlockPage))} />
      <Route path="/maker-config" element={createElement(withSuspense(MMConfig))} />
    </Routes>
  );
};
